
/**
 * Utilitaire centralisé pour gérer l'avant-première exclusive
 * Source de vérité unique pour la visibilité des produits
 */

export interface Product {
  id: string;
  name: string;
  isExclusivePreview?: boolean;
  [key: string]: any;
}

/**
 * ⚠️ SOURCE DE VÉRITÉ UNIQUE ⚠️
 * Date de fin de l'avant-première exclusive
 * Format: ISO 8601 avec timezone Europe/Paris
 * L'accès est actif tant que now < EXCLUSIVE_PREVIEW_END
 * 
 * AVANT-PREMIÈRE TERMINÉE - Date mise dans le passé pour désactiver immédiatement
 */
export const EXCLUSIVE_PREVIEW_END = "2025-01-20T00:00:00+01:00";

/**
 * Vérifie si la période d'avant-première est active
 */
export const isExclusivePreviewActive = (): boolean => {
  const now = new Date();
  const endDate = new Date(EXCLUSIVE_PREVIEW_END);
  return now < endDate;
};

/**
 * Vérifie si un produit est visible pour le public
 * Retourne false si le produit est en avant-première exclusive ET que la période est active
 */
export const isProductVisibleToPublic = (product: Product): boolean => {
  if (!product.isExclusivePreview) {
    return true;
  }
  
  // Si le produit est en avant-première ET que la période est active, il n'est PAS visible au public
  return !isExclusivePreviewActive();
};

/**
 * Filtre une liste de produits pour ne garder que ceux visibles au public
 * CETTE FONCTION DOIT ÊTRE UTILISÉE PARTOUT où des produits sont affichés
 */
export const filterPublicProducts = <T extends Product>(products: T[]): T[] => {
  return products.filter(isProductVisibleToPublic);
};

/**
 * Fonction globale pour obtenir tous les produits visibles au public
 * À utiliser comme source de vérité unique dans tout le projet
 */
export const getVisibleProducts = <T extends Product>(allProducts: T[]): T[] => {
  return filterPublicProducts(allProducts);
};

/**
 * Filtre une liste de produits pour ne garder que ceux en avant-première exclusive
 */
export const filterExclusivePreviewProducts = <T extends Product>(products: T[]): T[] => {
  if (!isExclusivePreviewActive()) {
    return [];
  }
  return products.filter(product => product.isExclusivePreview === true);
};
